import { Popover, Transition } from "@headlessui/react";
import {
  BgImage,
  Carousel,
  ContactCTA,
  Header,
  Intro,
  Markdown,
  SustainabilityCTA,
} from "@prmf/site-components";
import { routes, t } from "@prmf/site-router";
import { clsx } from "clsx";
import { Fragment } from "react";

const partners = [
  "media/assets/appliances-logos-aeg.jpg",
  "media/assets/appliances-logos-beko.png",
  "media/assets/appliances-logos-bosch.jpg",
  "media/assets/appliances-logos-caple.jpg",
  "media/assets/appliances-logos-electrolux.jpg",
  "media/assets/appliances-logos-miele.jpg",
  "media/assets/appliances-logos-siemens.jpg",
];

export default function Appliances() {
  return (
    <div>
      <Header
        title={t(routes.appliances, "title")}
        image="/media/assets/appliances-header.jpg"
        bgColor="black"
        className="text-white"
      >
        <p>
          At Paula Rosa Manhattan, we work closely with our appliance suppliers
          to create the best combination of technology and price point for our
          customers.
        </p>
        <p>
          A kitchen truly transforms a house into a home. Its a place where
          people eat, cook, entertain, bake and make lasting memories with
          friends and family. Kitchen appliances transform the landscape of a
          kitchen, offering design, functionality, and innovation.
        </p>
      </Header>
      <Intro title="Innovation and functional sophistication" bgColor="black">
        <p className="max-w-2xl">
          Be inspired to create a dream kitchen without boundaries or
          limitations, just endless possibilities. Kitchen appliances transform
          the landscape of a kitchen, offering design, functionality, and
          innovation.
        </p>
      </Intro>
      <section className="space-y-4 bg-[#f2f2f2] py-10">
        <h3 className="text-center font-semibold">
          Appliance partners include
        </h3>
        <Carousel
          className="container flex items-center justify-center gap-8 text-gray-100"
          pagination={false}
        >
          {partners.map((p) => (
            <BgImage
              key={p}
              className="mx-auto aspect-video w-[150px] bg-contain"
              src={p}
            />
          ))}
        </Carousel>
      </section>
      <section className="bg-black text-sm text-white">
        <Popover className="grid gap-10 md:container md:grid-cols-2">
          <div className="space-y-4 place-self-center pt-10 text-center max-md:container md:py-20">
            <h4 className="mx-auto max-w-sm text-2xl font-light">
              Make cleaning effortless with pyrolytic self-cleaning ovens
            </h4>
            <p className="max-w-md">
              With one touch of the pyrolytic self-cleaning function, dirt,
              grease and food residue in the oven is converted into ash that you
              can easily wipe off with a damp cloth. Why not consider an oven
              with this capability within your next development project.
            </p>
            <p>
              See how pyrolytic self-cleaning works on the range of AEG ovens.
            </p>
            <Popover.Button className="rounded-full border px-4 py-3">
              Click here
            </Popover.Button>
          </div>
          <div className="relative">
            <BgImage
              className="aspect-video h-full"
              src="media/assets/appliances-oven-feature.jpg"
            />
            <Transition
              as={Fragment}
              enter="transition ease-in duration-1000"
              leave="transition ease-out duration-1000"
              enterFrom="opacity-0"
              leaveTo="opacity-0"
            >
              <Popover.Panel className="absolute inset-0 z-40">
                <iframe
                  src="https://player.vimeo.com/video/519210951?autoplay=1&title=0&byline=0&portrait=0&background=1"
                  className="relative aspect-video h-full max-h-[500px] rounded-lg bg-black"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </Popover.Panel>
            </Transition>
          </div>
        </Popover>
      </section>
      <section className="py-20">
        <ul
          className={clsx(
            "container grid gap-6 text-sm !leading-relaxed",
            "sm:text-xs",
            "md:grid-cols-3",
            "lg:gap-10 lg:text-sm",
          )}
        >
          {[
            [
              "Cooking",
              "Induction hobs ensure the quick and efficient preparation of food in the kitchen. This is because the heat is transferred to the base of the pan rather than the designated cooking surface, which also makes them easier to clean.\n\nAdditional features include touch controls, flexible cooking zones, child safety locks and pause functions. You can also choose hobs that are automatically linked to your extractor hood for maximum convenience and effectiveness. The AEG range of Hob2Hood models are perfect for this.",
              "/media/assets/appliances-cooking.jpg",
            ],
            [
              "Cooling",
              "Refrigerators chill food quickly to keep food fresher for longer, reducing food waste.\n\nAdjustable shelves allow you to arrange bulky and tall items to suit your needs. Defrosting a freezer is now a thing of the past with zero frost technology. The temperatures in the fridge and freezer can now be adjusted separately for even more control.",
              "/media/assets/appliances-cooling.jpg",
            ],
            [
              "Dishwashing",
              "Dishwashers take away the hassle out of washing up after every meal saving time, water and energy.\n\n Models now available have great features which include adjustable racks, glass care systems and cutlery drawers as well as wash sensors that control the temperature and the amount of water required. The AEG ComfortLift® Dishwasher even has a lower basket which lifts up to enable easier loading and unloading.",
              "/media/assets/appliances-dishwasher.jpg",
            ],
          ].map(([title, text, img]) => (
            <li key={title}>
              <BgImage className="aspect-video" src={img} />
              <h4 className="my-2 border-b pb-1 text-sm font-medium">
                {title}
              </h4>
              <Markdown className="space-y-4" children={text!} />
            </li>
          ))}
        </ul>
      </section>
      <section className="bg-black text-center text-white">
        <BgImage
          src="/media/assets/appliances-lifestyle.jpg"
          className="aspect-video"
        />
        <h4 className="container py-20 text-lg font-semibold">
          Ensuring home builders have an unrivalled choice of appliances for
          their projects
        </h4>
      </section>
      <SustainabilityCTA />
      <ContactCTA />
    </div>
  );
}
